import * as BABYLON from '@babylonjs/core';
import { calculateLux, DEFAULT_EFFICIENCY } from '../../utils/luxCalculation';

// Smooth heatmap gradient: Dark Blue → Cyan → Green → Yellow → Orange → Red
const GRADIENT = [
  [0.00, 10, 10, 80],
  [0.10, 20, 40, 160],
  [0.20, 0, 120, 200],
  [0.35, 0, 190, 120],
  [0.50, 40, 210, 40],
  [0.65, 160, 220, 0],
  [0.78, 255, 200, 0],
  [0.90, 255, 120, 0],
  [1.00, 220, 30, 20]
];

function lerpColor(t) {
  t = Math.max(0, Math.min(1, t));
  for (let i = 0; i < GRADIENT.length - 1; i++) {
    if (t >= GRADIENT[i][0] && t <= GRADIENT[i + 1][0]) {
      const f = (t - GRADIENT[i][0]) / (GRADIENT[i + 1][0] - GRADIENT[i][0]);
      return [
        Math.round(GRADIENT[i][1] + (GRADIENT[i + 1][1] - GRADIENT[i][1]) * f),
        Math.round(GRADIENT[i][2] + (GRADIENT[i + 1][2] - GRADIENT[i][2]) * f),
        Math.round(GRADIENT[i][3] + (GRADIENT[i + 1][3] - GRADIENT[i][3]) * f)
      ];
    }
  }
  return [GRADIENT[GRADIENT.length - 1][1], GRADIENT[GRADIENT.length - 1][2], GRADIENT[GRADIENT.length - 1][3]];
}

const ASPECT_RATIOS = {
  '16:9': 16 / 9, '16:10': 16 / 10, '4:3': 4 / 3,
  '21:9': 21 / 9, '1:1': 1, '2.35:1': 2.35
};

const SAMPLE_RES = 24; // Grid sampling resolution
const TEX_SIZE = 1024; // Texture resolution for smooth gradients
const MAX_LUX = 600;

function getStackCount(proj) {
  const s = proj.stacking;
  if (!s || !s.enabled) return 1;
  return 1 + (parseInt(s.top) || 0) + (parseInt(s.bottom) || 0) + (parseInt(s.left) || 0) + (parseInt(s.right) || 0);
}

function getBeam(proj) {
  // Stacking multiplies effective lumens (virtual brightness boost — no extra beams rendered).
  const lumens = (proj.lumens || 10000) * getStackCount(proj);
  const td = proj.throw_distance || 5;
  const tr = proj.throw_ratio || 1.5;
  const aspect = ASPECT_RATIOS[proj.aspect_ratio] || ASPECT_RATIOS['16:10'];
  const rot = proj.rotation || { x: 0, y: 0, z: 0 };
  const pos = new BABYLON.Vector3(proj.position?.x || 0, proj.position?.y || 0, proj.position?.z || 0);
  
  // Rotations stored in DEGREES — convert to radians
  const rx = BABYLON.Tools.ToRadians(rot.x || 0);
  const ry = BABYLON.Tools.ToRadians(rot.y || 0);
  const rz = BABYLON.Tools.ToRadians(rot.z || 0);
  const rm = BABYLON.Matrix.RotationYawPitchRoll(ry, rx, rz);

  let fwd, right, up;

  if (proj.lensType === 'lens90') {
    // Lens90: projection exits from the side, not forward
    const lens90Dir = proj.lens90Rotation || 'right';
    let localDir, localUp;
    switch (lens90Dir) {
      case 'left':   localDir = new BABYLON.Vector3(-1, 0, 0); localUp = new BABYLON.Vector3(0, 1, 0); break;
      case 'right':  localDir = new BABYLON.Vector3(1, 0, 0);  localUp = new BABYLON.Vector3(0, 1, 0); break;
      case 'top':    localDir = new BABYLON.Vector3(0, 1, 0);  localUp = new BABYLON.Vector3(0, 0, 1); break;
      case 'bottom': localDir = new BABYLON.Vector3(0, -1, 0); localUp = new BABYLON.Vector3(0, 0, -1); break;
      default:       localDir = new BABYLON.Vector3(1, 0, 0);  localUp = new BABYLON.Vector3(0, 1, 0); break;
    }
    fwd = BABYLON.Vector3.TransformNormal(localDir, rm).normalize();
    up = BABYLON.Vector3.TransformNormal(localUp, rm).normalize();
    right = BABYLON.Vector3.Cross(fwd, up).normalize();
  } else if (proj.lensType === 'topBottom') {
    // Periscope folds back; beam exits along -Z local.
    const lensShiftH = (proj.lens_shift_h || 0) / 100;
    const lensShiftV = (proj.lens_shift_v || 0) / 100;
    const baseDir = new BABYLON.Vector3(-lensShiftH * 0.5, lensShiftV * 0.5, -1).normalize();
    fwd = BABYLON.Vector3.TransformNormal(baseDir, rm).normalize();
    up = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 1, 0), rm).normalize();
    right = BABYLON.Vector3.Cross(fwd, up).normalize();
  } else if (proj.lensType === 'ust') {
    // UST rear-throw: ~35° upward beam from front lens.
    const angle = 35 * Math.PI / 180;
    const sinA = Math.sin(angle);
    const cosA = Math.cos(angle);
    const lensShiftH = (proj.lens_shift_h || 0) / 100;
    const lensShiftV = (proj.lens_shift_v || 0) / 100;
    const baseDir = new BABYLON.Vector3(lensShiftH * 0.5, sinA + lensShiftV * 0.3, -cosA).normalize();
    const baseUp = new BABYLON.Vector3(0, cosA, sinA).normalize();
    fwd = BABYLON.Vector3.TransformNormal(baseDir, rm).normalize();
    up = BABYLON.Vector3.TransformNormal(baseUp, rm).normalize();
    right = BABYLON.Vector3.Cross(fwd, up).normalize();
  } else {
    // Standard lens with lens shift (also handles topBottom periscope —
    // beam direction is the same; only the lens origin is offset, which
    // doesn't affect lux falloff math meaningfully at meter+ distances).
    const lensShiftH = (proj.lens_shift_h || 0) / 100;
    const lensShiftV = (proj.lens_shift_v || 0) / 100;
    const baseDir = new BABYLON.Vector3(lensShiftH * 0.5, lensShiftV * 0.5, 1).normalize();
    fwd = BABYLON.Vector3.TransformNormal(baseDir, rm).normalize();
    up = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 1, 0), rm).normalize();
    right = BABYLON.Vector3.Cross(fwd, up).normalize();
  }

  return { pos, fwd, right, up, lumens, td, tr, aspect };
}

function luxAtPoint(pt, projectors) {
  let total = 0;
  for (const proj of projectors) {
    if (!proj.power_on) continue;
    const b = getBeam(proj);
    const toP = pt.subtract(b.pos);
    const dist = BABYLON.Vector3.Dot(toP, b.fwd);
    if (dist < 0.1) continue;

    const offset = toP.subtract(b.fwd.scale(dist));
    const u = BABYLON.Vector3.Dot(offset, b.right);
    const v = BABYLON.Vector3.Dot(offset, b.up);

    const halfW = (dist / b.tr) / 2;
    const halfH = halfW / b.aspect;
    if (Math.abs(u) > halfW || Math.abs(v) > halfH) continue;

    // Positions are in meters — area is directly in m²
    const area = (dist / b.tr) * ((dist / b.tr) / b.aspect);
    const baseLux = calculateLux(b.lumens, area);

    // Smooth edge falloff
    const eu = Math.abs(u) / halfW;
    const ev = Math.abs(v) / halfH;
    const edge = Math.max(eu, ev);
    const falloff = edge > 0.8 ? Math.cos((edge - 0.8) / 0.2 * Math.PI * 0.5) : 1.0;
    total += baseLux * Math.max(falloff, 0);
  }
  return total;
}

// Bilinear interpolation for smooth texture
function bilinearSample(grid, fx, fy) {
  const rows = grid.length, cols = grid[0].length;
  const x = fx * (cols - 1);
  const y = fy * (rows - 1);
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, cols - 1), y1 = Math.min(y0 + 1, rows - 1);
  const dx = x - x0, dy = y - y0;
  return (
    grid[y0][x0] * (1 - dx) * (1 - dy) +
    grid[y0][x1] * dx * (1 - dy) +
    grid[y1][x0] * (1 - dx) * dy +
    grid[y1][x1] * dx * dy
  );
}

// ==================== FACE DEFINITIONS ====================
function getFacePlanes(obj) {
  const pos = obj.position || { x: 0, y: 0, z: 0 };
  const dim = obj.dimensions || { width: 1, height: 1, depth: 1 };
  const rot = obj.rotation || { x: 0, y: 0, z: 0 };
  const w = dim.width || 1, h = dim.height || 1, d = dim.depth || 1;
  const tp = (obj.type || '').toLowerCase();

  const faces = [];

  if (tp === 'plane' || tp === 'projection_screen' || tp === 'scrim_screen') {
    faces.push({
      name: 'Front', pw: w, ph: h, offset: [0, 0, 0], rotEuler: [0, 0, 0]
    });
  } else {
    // Box: 6 faces — each with local offset and rotation relative to object center
    // CreatePlane default normal is (0,0,-1) in Babylon.js left-handed system
    // Rotations orient the front face OUTWARD from the box
    faces.push({ name: 'Front',  pw: w, ph: h, offset: [0, 0, -(d/2 + 0.015)], rotEuler: [0, 0, 0] });
    faces.push({ name: 'Back',   pw: w, ph: h, offset: [0, 0, (d/2 + 0.015)],  rotEuler: [0, Math.PI, 0] });
    faces.push({ name: 'Left',   pw: d, ph: h, offset: [-(w/2 + 0.015), 0, 0], rotEuler: [0, Math.PI/2, 0] });
    faces.push({ name: 'Right',  pw: d, ph: h, offset: [(w/2 + 0.015), 0, 0],  rotEuler: [0, -Math.PI/2, 0] });
    faces.push({ name: 'Top',    pw: w, ph: d, offset: [0, (h/2 + 0.015), 0],  rotEuler: [Math.PI/2, 0, 0] });
    faces.push({ name: 'Bottom', pw: w, ph: d, offset: [0, -(h/2 + 0.015), 0], rotEuler: [-Math.PI/2, 0, 0] });
  }

  return { faces, objPos: new BABYLON.Vector3(pos.x, pos.y, pos.z), objRot: rot, dim: { w, h, d } };
}

// Compute world position for a UV point on a face
function faceWorldPoint(u, v, face, objPos, objRot) {
  // Local position on the face plane (centered)
  const localX = (u - 0.5) * face.pw;
  const localY = (v - 0.5) * face.ph;
  
  // Face local offset
  const faceLocal = new BABYLON.Vector3(
    face.offset[0] + localX * Math.cos(face.rotEuler[1]) + 0,
    face.offset[1] + localY * (face.name === 'Top' || face.name === 'Bottom' ? 0 : 1),
    face.offset[2] + localX * Math.sin(face.rotEuler[1]) + 0
  );

  // Actually, let me compute this properly using rotation matrices
  // Face plane: the plane is face.pw x face.ph, positioned at face.offset
  // with rotation face.rotEuler applied to face the right direction
  
  // Point on the unrotated plane (in XY plane at Z=0)
  const planePoint = new BABYLON.Vector3(localX, localY, 0);
  
  // Rotate by face euler
  const faceRotMat = BABYLON.Matrix.RotationYawPitchRoll(face.rotEuler[1], face.rotEuler[0], face.rotEuler[2]);
  const rotatedPoint = BABYLON.Vector3.TransformCoordinates(planePoint, faceRotMat);
  
  // Add face offset
  const localPos = rotatedPoint.add(new BABYLON.Vector3(face.offset[0], face.offset[1], face.offset[2]));
  
  // Apply object rotation (degrees → radians)
  const objRotMat = BABYLON.Matrix.RotationYawPitchRoll(
    BABYLON.Tools.ToRadians(objRot.y || 0),
    BABYLON.Tools.ToRadians(objRot.x || 0),
    BABYLON.Tools.ToRadians(objRot.z || 0)
  );
  const worldLocal = BABYLON.Vector3.TransformCoordinates(localPos, objRotMat);
  
  return worldLocal.add(objPos);
}

// ==================== MAIN SYSTEM ====================
export class LuxHeatmapSystem {
  constructor(scene) {
    this.scene = scene;
    this.enabled = false;
    this.meshes = [];
    this.legendEl = null;
  }

  enable(sceneObjects, projectors) {
    if (this.enabled) this.disable();
    this.enabled = true;

    const surfaces = sceneObjects.filter(o =>
      !['projector', 'reverse_projector', 'truss_system', 'scaffolding', 'led_screen', 'mirror'].includes(o.type)
    );

    const activeProjectors = projectors.filter(p => p.power_on);
    if (activeProjectors.length === 0) { this.showLegend(); return; }

    for (const obj of surfaces) {
      const { faces, objPos, objRot } = getFacePlanes(obj);

      for (const face of faces) {
        // Sample lux grid
        const grid = [];
        for (let gy = 0; gy < SAMPLE_RES; gy++) {
          grid[gy] = [];
          for (let gx = 0; gx < SAMPLE_RES; gx++) {
            const u = (gx + 0.5) / SAMPLE_RES;
            const v = (gy + 0.5) / SAMPLE_RES;
            const wp = faceWorldPoint(u, v, face, objPos, objRot);
            grid[gy][gx] = luxAtPoint(wp, activeProjectors);
          }
        }

        // Create smooth texture using bilinear interpolation at lower res then upscale
        const RENDER_SIZE = 256; // Render at lower res for performance
        const canvas = document.createElement('canvas');
        canvas.width = RENDER_SIZE;
        canvas.height = RENDER_SIZE;
        const ctx = canvas.getContext('2d');

        // Draw smooth gradient at render resolution
        // Canvas (0,0) is top-left; mesh top should show wall top (high v).
        // Flip fy so canvas top → grid row last (wall top).
        const imgData = ctx.createImageData(RENDER_SIZE, RENDER_SIZE);
        for (let py = 0; py < RENDER_SIZE; py++) {
          for (let px = 0; px < RENDER_SIZE; px++) {
            const fx = px / RENDER_SIZE;
            const fy = 1.0 - (py / RENDER_SIZE);
            const lux = bilinearSample(grid, fx, fy);
            const t = Math.min(lux / MAX_LUX, 1.0);
            const c = lerpColor(t);
            const idx = (py * RENDER_SIZE + px) * 4;
            imgData.data[idx] = c[0];
            imgData.data[idx + 1] = c[1];
            imgData.data[idx + 2] = c[2];
            imgData.data[idx + 3] = 210;
          }
        }
        ctx.putImageData(imgData, 0, 0);

        // Upscale to final texture with smooth interpolation
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = TEX_SIZE;
        finalCanvas.height = TEX_SIZE;
        const fctx = finalCanvas.getContext('2d');
        fctx.imageSmoothingEnabled = true;
        fctx.imageSmoothingQuality = 'high';
        fctx.drawImage(canvas, 0, 0, TEX_SIZE, TEX_SIZE);

        // Draw lux value labels on the final canvas
        // Flip Y for labels to match the flipped gradient above
        const labelStep = Math.max(3, Math.floor(SAMPLE_RES / 5));
        fctx.textAlign = 'center';
        fctx.textBaseline = 'middle';
        for (let gy = Math.floor(labelStep / 2); gy < SAMPLE_RES; gy += labelStep) {
          for (let gx = Math.floor(labelStep / 2); gx < SAMPLE_RES; gx += labelStep) {
            const lux = grid[gy][gx];
            const cx = ((gx + 0.5) / SAMPLE_RES) * TEX_SIZE;
            const cy = (1.0 - (gy + 0.5) / SAMPLE_RES) * TEX_SIZE;

            // White text with dark shadow
            const label = lux >= 1 ? lux.toFixed(1) : '0.0';
            fctx.font = 'bold 28px Arial';
            fctx.fillStyle = 'rgba(0,0,0,0.6)';
            fctx.fillText(label, cx + 1.5, cy + 1.5);
            fctx.fillStyle = '#ffffff';
            fctx.fillText(label, cx, cy);
          }
        }

        // Create Babylon texture from final canvas
        const tex = new BABYLON.DynamicTexture(`hm_${obj.id}_${face.name}`, { width: TEX_SIZE, height: TEX_SIZE }, this.scene, false);
        const texCtx = tex.getContext();
        texCtx.drawImage(finalCanvas, 0, 0);
        tex.update();

        // Create plane mesh
        const plane = BABYLON.MeshBuilder.CreatePlane(`hmPlane_${obj.id}_${face.name}`, {
          width: face.pw, height: face.ph
        }, this.scene);

        // Position: apply face offset, then object rotation (degrees → radians), then object position
        const faceOffset = new BABYLON.Vector3(face.offset[0], face.offset[1], face.offset[2]);
        const orx = BABYLON.Tools.ToRadians(objRot.x || 0);
        const ory = BABYLON.Tools.ToRadians(objRot.y || 0);
        const orz = BABYLON.Tools.ToRadians(objRot.z || 0);
        const objRotMat = BABYLON.Matrix.RotationYawPitchRoll(ory, orx, orz);
        const worldOffset = BABYLON.Vector3.TransformCoordinates(faceOffset, objRotMat);
        plane.position = objPos.add(worldOffset);

        // Rotation: face rotation + object rotation (degrees → radians)
        const faceQuat = BABYLON.Quaternion.FromEulerAngles(face.rotEuler[0], face.rotEuler[1], face.rotEuler[2]);
        const objQuat = BABYLON.Quaternion.FromEulerAngles(orx, ory, orz);
        plane.rotationQuaternion = objQuat.multiply(faceQuat);

        // Material
        const mat = new BABYLON.StandardMaterial(`hmMat_${obj.id}_${face.name}`, this.scene);
        mat.diffuseTexture = tex;
        mat.emissiveTexture = tex;
        mat.emissiveColor = new BABYLON.Color3(1, 1, 1);
        mat.disableLighting = true;
        mat.backFaceCulling = false;
        mat.useAlphaFromDiffuseTexture = true;
        mat.alpha = 0.88;
        mat.zOffset = -2; // Render slightly in front to avoid z-fighting
        plane.material = mat;
        plane.isPickable = false;

        this.meshes.push(plane);
      }
    }

    this.showLegend();
  }

  disable() {
    this.enabled = false;
    for (const m of this.meshes) {
      if (m.material) {
        if (m.material.diffuseTexture) m.material.diffuseTexture.dispose();
        m.material.dispose();
      }
      m.dispose();
    }
    this.meshes = [];
    this.hideLegend();
  }

  showLegend() {
    if (this.legendEl) return;
    const el = document.createElement('div');
    el.id = 'lux-heatmap-legend';
    el.style.cssText = `
      position:fixed;top:80px;left:16px;z-index:1000;
      background:rgba(10,10,20,0.93);border-radius:8px;
      padding:12px 16px;color:white;font-family:Arial,sans-serif;
      font-size:11px;pointer-events:none;backdrop-filter:blur(10px);
      border:1px solid rgba(200,50,200,0.35);box-shadow:0 4px 20px rgba(0,0,0,0.5);
    `;

    // Build legend with safe DOM APIs instead of innerHTML (XSS prevention)
    const title = document.createElement('div');
    title.style.cssText = 'font-weight:bold;margin-bottom:8px;font-size:13px;color:#e0b0ff;letter-spacing:0.5px';
    title.textContent = 'lux';
    el.appendChild(title);

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;align-items:stretch';

    // Gradient bar
    const barWrap = document.createElement('div');
    barWrap.style.cssText = 'width:20px;height:180px;border-radius:4px;overflow:hidden;border:1px solid rgba(255,255,255,0.15)';
    const bar = document.createElement('div');
    bar.style.cssText = 'width:100%;height:100%;background:linear-gradient(to bottom,' +
      'rgb(220,30,20),rgb(255,120,0),rgb(255,200,0),rgb(160,220,0),rgb(40,210,40),rgb(0,190,120),rgb(0,120,200),rgb(20,40,160),rgb(10,10,80))';
    barWrap.appendChild(bar);
    row.appendChild(barWrap);

    // Scale labels
    const scale = document.createElement('div');
    scale.style.cssText = 'display:flex;flex-direction:column;justify-content:space-between;font-size:11px;font-weight:bold';
    const steps = [600, 525, 450, 375, 300, 225, 150, 75];
    for (const s of steps) {
      const t = s / MAX_LUX;
      const c = lerpColor(t);
      const label = document.createElement('div');
      label.style.color = `rgb(${c[0]},${c[1]},${c[2]})`;
      label.textContent = String(s);
      scale.appendChild(label);
    }
    row.appendChild(scale);
    el.appendChild(row);

    const formula = document.createElement('div');
    formula.style.cssText = 'margin-top:8px;font-size:9px;color:#777';
    formula.textContent = 'Lux = (Lumens \u00d7 0.70) / Area';
    el.appendChild(formula);

    document.body.appendChild(el);
    this.legendEl = el;
  }

  hideLegend() {
    if (this.legendEl) { this.legendEl.remove(); this.legendEl = null; }
  }

  dispose() { this.disable(); }
}
